import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import TextArea from "devextreme-react/text-area";
import closeIcon from "../../../images/closeIcon.svg";
import { baseRequest } from "../../../fetch/basicThunk";
import { QUERY_PUT_BILLING_PAYMENT_ORDER_APPROVAL } from "../../../fetch/constantQueries";
import { openModal } from "../../../redux/modal/modalSlice";

export default function RejectPaymentOrderModal({
  open,
  order,
  onClose,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (open && order) {
      setRejectionReason(order.rejectionReason || "");
      setErrorMessage("");
    } else {
      setRejectionReason("");
      setErrorMessage("");
    }
  }, [open, order]);

  const handleSubmit = async () => {
    if (!rejectionReason || !rejectionReason.trim()) {
      setErrorMessage(t("BillingPaymentOrders.ErrorRejectRequiredReason"));
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const payload = {
        status: "REJECTED",
        rejectionReason: rejectionReason.trim(),
      };

      await baseRequest(
        { param: `${order.id}/approval`, body: payload },
        dispatch,
        QUERY_PUT_BILLING_PAYMENT_ORDER_APPROVAL,
        "PUT"
      );

      setLoading(false);
      dispatch(
        openModal({
          open: true,
          title: t("BillingPaymentOrders.SuccessReject"),
          type: "success",
        })
      );
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setLoading(false);
      dispatch(
        openModal({
          open: true,
          title: t("BillingPaymentOrders.ErrorRejectOrder"),
          type: "error",
        })
      );
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        square: true,
        style: {
          borderRadius: 0,
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <div style={{ backgroundColor: "#FFFFFF", borderRadius: 0 }}>
        <DialogTitle
          disableTypography
          style={{
            padding: "1.25rem 1.5rem 0.5rem 1.5rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: "1.25rem",
              color: "#000000",
            }}
          >
            {t("BillingPaymentOrders.RejectModalTitle")}
          </span>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={loading}
            aria-label="close"
          >
            <img src={closeIcon} alt="close" style={{ width: "1rem", height: "1rem" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: "0.5rem 1.5rem 1.5rem 1.5rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <Typography
              variant="body2"
              style={{
                color: "#333333",
                fontSize: "0.875rem",
              }}
            >
              {t("BillingPaymentOrders.RejectModalInstruction")}
            </Typography>

            <div>
              <TextArea
                label={t("BillingPaymentOrders.RejectModalReasonLabel")}
                labelMode="floating"
                placeholder={t("BillingPaymentOrders.RejectModalReasonPlaceholder")}
                value={rejectionReason}
                valueChangeEvent="change"
                height={100}
                autoResizeEnabled={false}
                onValueChanged={(e) => {
                  setRejectionReason(e.value || "");
                  if (errorMessage) setErrorMessage("");
                }}
                disabled={loading}
              />
            </div>

            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 0,
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                backgroundColor: "#FFFFFF",
              }}
            >
              <div>
                <Typography variant="body2" style={{ color: "#000000" }}>
                  <strong style={{ fontWeight: 700 }}>
                    {t("BillingPaymentOrders.RejectModalAttachmentsTitle")}{" "}
                  </strong>
                  <span style={{ fontWeight: 400, color: "#666666" }}>
                    {t("BillingPaymentOrders.RejectModalAttachmentsOptional")}
                  </span>
                </Typography>
                <Typography
                  variant="caption"
                  style={{
                    color: "#555555",
                    display: "block",
                    marginTop: "2px",
                  }}
                >
                  {t("BillingPaymentOrders.RejectModalAttachmentsInstruction")}
                </Typography>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={loading}
                  style={{
                    textTransform: "none",
                    borderColor: "#007BC0",
                    color: "#007BC0",
                    borderRadius: 0,
                    padding: "4px 12px",
                    fontWeight: 500,
                  }}
                >
                  <ArrowUpwardIcon style={{ fontSize: 16, marginRight: "4px", color: "#007BC0" }} />
                  {t("BillingPaymentOrders.RejectModalChooseFile")}
                </Button>
                <Typography
                  variant="body2"
                  style={{
                    color: "#666666",
                    fontSize: "0.875rem",
                  }}
                >
                  {t("BillingPaymentOrders.RejectModalNoFileChosen")}
                </Typography>
              </div>
            </div>

            {errorMessage && (
              <Typography variant="body2" style={{ color: "#D50005", fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            )}
          </div>
        </DialogContent>

        <DialogActions
          style={{
            padding: "0.75rem 1.5rem 1.5rem 1.5rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading}
            style={{
              textTransform: "none",
              backgroundColor: "#007BC0",
              color: "#FFFFFF",
              borderRadius: 0,
              minWidth: "80px",
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("BillingPaymentOrders.RejectModalConfirm")
            )}
          </Button>
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            disabled={loading}
            style={{
              textTransform: "none",
              borderColor: "#007BC0",
              color: "#007BC0",
              borderRadius: 0,
              minWidth: "80px",
            }}
          >
            {t("BillingPaymentOrders.RejectModalCancel")}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
